import { supabase } from "@/lib/supabase";
import type { Database } from "@/lib/database.types";

type SubjectRow = Database["public"]["Tables"]["subjects"]["Row"];
type UnitRow = Database["public"]["Tables"]["units"]["Row"];
type TopicRow = Database["public"]["Tables"]["topics"]["Row"];

export type SubjectOutline = {
  subject: SubjectRow;
  units: Array<UnitRow & { topics: TopicRow[] }>;
};

function getTopicOrderValue(topic: TopicRow, subjectSlug: string): number {
  const slug = topic.slug ?? "";
  const prefix = `${subjectSlug}-`;
  const normalizedSlug = slug.startsWith(prefix) ? slug.slice(prefix.length) : slug;
  const slugMatch = normalizedSlug.match(/(?:topic-)?\d+-(\d+)/i);
  if (slugMatch?.[1]) {
    const parsed = parseInt(slugMatch[1], 10);
    if (!Number.isNaN(parsed)) return parsed;
  }
  const trailingNumberMatch = normalizedSlug.match(/(\d+)$/);
  if (trailingNumberMatch?.[1]) {
    const parsed = parseInt(trailingNumberMatch[1], 10);
    if (!Number.isNaN(parsed)) return parsed;
  }
  const title = topic.title ?? "";
  const titleMatch = title.match(/(\d+)\.(\d+)/);
  if (titleMatch?.[2]) {
    const parsed = parseInt(titleMatch[2], 10);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return Number.MAX_SAFE_INTEGER;
}

export async function fetchSubjects(): Promise<SubjectRow[]> {
  const { data, error } = await supabase
    .from("subjects")
    .select("*")
    .order("name", { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export async function fetchSubjectBySlug(slug: string): Promise<SubjectRow | null> {
  const { data, error } = await supabase
    .from("subjects")
    .select("*")
    .eq("slug", slug)
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}

export async function fetchSubjectOutline(slug: string): Promise<SubjectOutline> {
  const subject = await fetchSubjectBySlug(slug);
  if (!subject) {
    throw new Error(`Subject with slug "${slug}" not found`);
  }
  const { data: units, error: unitError } = await supabase
    .from("units")
    .select("*")
    .eq("subject_id", subject.id)
    .order("index", { ascending: true });
  if (unitError) throw unitError;

  const unitIds = (units ?? []).map((u) => u.id);
  let topics: TopicRow[] = [];
  if (unitIds.length) {
    const { data: topicData, error: topicError } = await supabase
      .from("topics")
      .select("*")
      .in("unit_id", unitIds)
      .order("slug", { ascending: true });
    if (topicError) throw topicError;
    topics = topicData ?? [];
  }

  const topicsByUnit = new Map<string, TopicRow[]>();
  topics.forEach((topic) => {
    const arr = topicsByUnit.get(topic.unit_id) ?? [];
    arr.push(topic);
    topicsByUnit.set(topic.unit_id, arr);
  });

  const outlineUnits = (units ?? []).map((unit) => {
    const unitTopics = topicsByUnit.get(unit.id) ?? [];
    const sortedTopics = [...unitTopics].sort((a, b) => {
      const orderA = getTopicOrderValue(a, subject.slug);
      const orderB = getTopicOrderValue(b, subject.slug);
      if (orderA !== orderB) return orderA - orderB;
      const labelA = a.title ?? a.slug ?? "";
      const labelB = b.title ?? b.slug ?? "";
      return labelA.localeCompare(labelB, undefined, { numeric: true });
    });
    return {
      ...unit,
      topics: sortedTopics,
    };
  });

  return { subject, units: outlineUnits };
}
